import { Children } from 'react';
import type { CSSProperties, FC, ReactNode } from 'react';
import { Button } from 'antd';
import { capitalCase } from 'change-case';
import { TbChevronRight } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import WorkspaceResourceAvatar from '../WorkspaceResourceAvatar';
import { normalizeWorkspaceResourceKind } from '../WorkspaceResourceAvatar/configuration';

interface WorkspaceResourceReferenceButtonProps {
    ariaLabel?: string;
    href?: string;
    listType?: string;
    nestedContext?: ReactNode | ReactNode[];
    nestedContextIcon?: ReactNode;
    onClick?: () => void;
    resourceName?: ReactNode;
    resourceType: string;
    resourceWebsite?: string;
    style?: CSSProperties;
}

const WorkspaceResourceReferenceButton: FC<WorkspaceResourceReferenceButtonProps> = ({
    ariaLabel,
    href,
    listType,
    nestedContext,
    nestedContextIcon,
    onClick,
    resourceName,
    resourceType,
    resourceWebsite,
    style,
}) => {
    const { token } = useAntDesignTheme();
    const navigate = useNavigate();
    const contextItems = (Array.isArray(nestedContext) ? nestedContext : [nestedContext]).filter(
        (item): item is ReactNode => item !== undefined && item !== null && item !== '',
    );
    const resolvedResourceName = resourceName ?? capitalCase(normalizeWorkspaceResourceKind(resourceType));
    const labelParts = [
        typeof resolvedResourceName === 'string' ? resolvedResourceName : undefined,
        ...contextItems.filter((item): item is string => typeof item === 'string'),
    ].filter(Boolean);

    return (
        <Button
            aria-label={ariaLabel ?? (labelParts.length ? `Open ${labelParts.join(', ')}` : 'Open resource')}
            disabled={!href && !onClick}
            onClick={() => {
                if (href) {
                    navigate(href);
                }

                onClick?.();
            }}
            size='small'
            style={{
                alignItems: 'center',
                display: 'inline-flex',
                fontSize: token.fontSizeSM,
                fontWeight: 500,
                gap: '0.25rem',
                height: '1.5rem',
                lineHeight: 1,
                maxWidth: '100%',
                overflow: 'hidden',
                verticalAlign: 'middle',
                ...style,
            }}
        >
            <WorkspaceResourceAvatar
                listType={listType}
                resource={resourceType}
                size={14}
                style={{ flex: 'none' }}
                website={resourceWebsite}
            />
            <span
                style={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {resolvedResourceName}
            </span>
            {Children.toArray(
                contextItems.map((item, index) => (
                    <span
                        style={{
                            alignItems: 'center',
                            display: 'inline-flex',
                            gap: '0.25rem',
                            minWidth: 0,
                        }}
                    >
                        <TbChevronRight
                            aria-hidden='true'
                            size='0.6875rem'
                            style={{ color: token.colorTextTertiary, flex: 'none' }}
                        />
                        {nestedContextIcon && index === contextItems.length - 1 ? (
                            <span
                                aria-hidden='true'
                                style={{
                                    alignItems: 'center',
                                    color: token.colorTextTertiary,
                                    display: 'inline-flex',
                                    flex: '0 0 auto',
                                    height: '0.875rem',
                                    justifyContent: 'center',
                                    width: '0.875rem',
                                }}
                            >
                                {nestedContextIcon}
                            </span>
                        ) : null}
                        <span
                            style={{
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {item}
                        </span>
                    </span>
                )),
            )}
        </Button>
    );
};

export default WorkspaceResourceReferenceButton;
